import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatTRY, downloadExport } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Search, FileSpreadsheet, ShoppingCart } from "lucide-react";
import { SalesList } from "@/components/sales/SalesList";
import { CustomerHistory } from "@/components/sales/CustomerHistory";
import { TopProducts } from "@/components/sales/TopProducts";
import { toast } from "sonner";

const PAY_FILTERS = ["Tümü", "Nakit", "Kredi Kartı", "Karma", "Açık Hesap", "Açık Hesap (tümü)", "Personel"];
const PRESETS = [["Bugün", 0], ["Bu Hafta", 1], ["Bu Ay", 2]];
const presetRange = (k) => {
  const now = new Date(); const tz = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });
  if (k === 0) return [tz(now), tz(now)];
  if (k === 1) { const d = new Date(now); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return [tz(d), tz(now)]; }
  const d = new Date(now); d.setDate(1); return [tz(d), tz(now)];
};
const localDay = (iso) => new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });

export default function SalesHistory() {
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pay, setPay] = useState("Tümü");
  const [limit, setLimit] = useState(50);
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("sale");

  const load = async () => { const r = await api.get("/sales"); setSales(r.data); return r.data; };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return sales.filter((s) => {
      if (focusId && s.id === focusId) return true;
      const d = localDay(s.created_at);
      if (start && d < start) return false;
      if (end && d > end) return false;
      if (pay === "Açık Hesap (tümü)" ? !s.was_open_tab : (pay !== "Tümü" && s.payment_type !== pay)) return false;
      if (ql && !(s.customer_name || "").toLowerCase().includes(ql) && !s.items.some((i) => i.product_name.toLowerCase().includes(ql))) return false;
      return true;
    });
  }, [sales, q, start, end, pay, focusId]);

  const sum = useMemo(() => filtered.reduce((a, s) => {
    const paid = s.payment_type !== "Personel" && s.payment_type !== "Açık Hesap";
    return { total: a.total + (paid ? s.total : 0), profit: a.profit + (paid ? s.profit : 0), items: a.items + s.items.reduce((x, i) => x + i.quantity, 0),
      open: a.open + (s.payment_type === "Açık Hesap" ? s.total : 0), tabClosed: a.tabClosed + (s.was_open_tab && paid ? s.total : 0) };
  }, { total: 0, profit: 0, items: 0, open: 0, tabClosed: 0 }), [filtered]);

  const exportXlsx = async () => {
    try { await downloadExport("/export/sales", { start: start || undefined, end: end || undefined }, "fithouse_satislar.xlsx"); toast.success("Excel dosyası indirildi"); }
    catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Satış Geçmişi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Tüm fişler ve müşteri geçmişi — düzenleme ve iade buradan da yapılabilir</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" asChild data-testid="go-pos-btn"><Link to="/satis"><ShoppingCart className="mr-1.5 h-4 w-4" /> Kasaya Dön</Link></Button>
          <Button variant="secondary" data-testid="export-history-btn" onClick={exportXlsx}><FileSpreadsheet className="mr-1.5 h-4 w-4 text-primary" /> Excel'e Aktar</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-end gap-3" data-testid="history-filters">
          <div className="flex basis-full gap-1.5" data-testid="history-presets">
            {PRESETS.map(([l, k]) => { const [s0, e0] = presetRange(k); const on = start === s0 && end === e0; return (
              <button key={l} type="button" data-testid={`history-preset-${k}`} onClick={() => { setStart(s0); setEnd(e0); }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{l}</button>); })}
            <button type="button" data-testid="history-preset-all" onClick={() => { setStart(""); setEnd(""); }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${!start && !end ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>Tümü</button>
          </div>
          <div className="relative min-w-56 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Müşteri veya ürün ara…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" data-testid="history-search" />
          </div>
          <div><label className="text-[11px] text-muted-foreground">Başlangıç</label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="history-start" /></div>
          <div><label className="text-[11px] text-muted-foreground">Bitiş</label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="history-end" /></div>
          <Select value={pay} onValueChange={setPay}>
            <SelectTrigger className="h-9 w-40" data-testid="history-payment"><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_FILTERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          {(q || start || end || pay !== "Tümü") && <Button variant="ghost" size="sm" data-testid="history-clear" onClick={() => { setQ(""); setStart(""); setEnd(""); setPay("Tümü"); }}>Temizle</Button>}
        </div>
        <div className="mt-4 flex flex-wrap gap-6 text-sm" data-testid="history-summary">
          <span><span className="text-muted-foreground">Fiş:</span> <b>{filtered.length}</b></span>
          <span><span className="text-muted-foreground">Ürün:</span> <b>{sum.items}</b></span>
          <span><span className="text-muted-foreground">Toplam (tahsil edilen):</span> <b className="text-primary">{formatTRY(sum.total)}</b></span>
          <span><span className="text-muted-foreground">Kâr:</span> <b>{formatTRY(sum.profit)}</b></span>
          {sum.open > 0 && <span><span className="text-muted-foreground">Bekleyen (açık hesap):</span> <b className="text-amber-500" data-testid="history-open-total">{formatTRY(sum.open)}</b></span>}
          {sum.tabClosed > 0 && <span><span className="text-muted-foreground">Kapanan açık hesaplar:</span> <b className="text-amber-600" data-testid="history-tab-closed-total">{formatTRY(sum.tabClosed)}</b></span>}
        </div>
      </div>

      <TopProducts sales={filtered.filter((s) => s.payment_type !== "Personel" && s.payment_type !== "Açık Hesap")} />

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Receipt className="h-5 w-5" /> Fişler</h3>
        <div className="mt-4">
          <SalesList sales={focusId ? [...filtered.filter((s) => s.id === focusId), ...filtered.filter((s) => s.id !== focusId)].slice(0, limit) : filtered.slice(0, limit)} onChanged={load} focusId={focusId} emptyText="Filtreye uyan satış yok" />
        </div>
        {filtered.length > limit && (
          <Button variant="secondary" className="mt-4 w-full" data-testid="history-more" onClick={() => setLimit(limit + 50)}>Daha fazla göster ({filtered.length - limit} kaldı)</Button>
        )}
      </div>

      <CustomerHistory refreshKey={sales.map((s) => `${s.id}:${s.customer_name}:${s.total}`).join("|")} />
    </div>
  );
}
